import React, { useState, useEffect } from 'react'
import CallToActionCards from './CallToActionCards'
import CreditReport from './CreditReport'
import FinancialReport from './FinancialReport'
import DocumentCard from './DocumentCard'
import PaymentPlanSummary from './PaymentPlanSummary'
import ProfileEdit from './ProfileEdit'
import AccountActions from './AccountActions'
import AffiliateOfferCards from './AffiliateOfferCards'
import { Card, CardContent, CardTitle, CardDescription } from '../ui/Card'
import { Badge } from '../ui/Badge'
import Alert from '../shared/Alert'

const Dashboard = () => {
  const [cases, setCases] = useState([])
  const [unorganizedDocuments, setUnorganizedDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [creditReport, setCreditReport] = useState(null)
  const [financialReport, setFinancialReport] = useState(null)
  const [creditReportSolosettles, setCreditReportSolosettles] = useState([])
  const [affiliateOffers, setAffiliateOffers] = useState([])
  const [activeTab, setActiveTab] = useState('all')
  const [userProfile, setUserProfile] = useState(null)
  const [alertParams, setAlertParams] = useState({ success: null, message: null })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/my_dashboard.json', {
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
        })
        const data = await response.json()
        setCases(data.cases)
        setCreditReport(data.credit_report)
        setFinancialReport(data.financial_report)
        setUnorganizedDocuments(data.unorganizedDocuments)
        setUserProfile(data.user_profile)
        setCreditReportSolosettles(data.credit_report_solosettles || [])
        setAffiliateOffers(data.affiliate_offers || [])
      } catch (error) {
        console.error('Error fetching dashboard data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const url = new URL(window.location)
    const tabParam = url.searchParams.get('activeTab')
    if (tabParam) {
      setActiveTab(tabParam)

      if (url.searchParams.has('redirect_url')) {
        const redirectUrl = url.searchParams.get('redirect_url')
        if (redirectUrl.includes('credit_report')) {
          sessionStorage.setItem('fromCreditReport', 'true')
        }

        url.searchParams.delete('redirect_url')
        window.history.replaceState({}, '', url)
      }
    }

    const success = url.searchParams.get('success')
    const message = url.searchParams.get('message')
    if (message) {
      setAlertParams({
        success: success === 'true' ? true : success === 'false' ? false : null,
        message,
      })

      // Delay URL cleanup so page doesn't reload
      setTimeout(() => {
        const currentUrl = new URL(window.location)
        currentUrl.searchParams.delete('success')
        currentUrl.searchParams.delete('message')
        window.history.replaceState({}, '', currentUrl)
      }, 5000)
    }
  }, [])

  const formatMoney = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const CaseHeader = ({ info }) => {
    if (info.plaintiff_name && info.lawsuit_amount) {
      return (
        <div className='w-100 d-flex justify-content-between align-items-center'>
          <h2 className='mb-0'>{info.plaintiff_name}</h2>
          <h2 className='mb-0 text-primary'>{formatMoney(info.lawsuit_amount)}</h2>
        </div>
      )
    } else if (info.civil_number) {
      return <h2 className='mb-0'>Case {info.civil_number}</h2>
    } else {
      return <h2 className='mb-0'>Case #{info.id}</h2>
    }
  }

  const handleProfileUpdate = updatedProfile => {
    setUserProfile(prev => ({
      ...prev,
      ...updatedProfile,
    }))
  }

  const handleTabChange = tab => {
    const newTab = activeTab === tab ? 'all' : tab
    setActiveTab(newTab)
    const url = new URL(window.location)
    url.searchParams.set('activeTab', newTab)
    window.history.pushState({}, '', url)
  }

  return (
    <div className='container mb-13'>
      <div className='row'>
        <div className='col'>
          <h1 className='mb-3 font-serif'>Dashboard</h1>
          <div
            className='d-flex gap-2 mb-5 overflow-auto flex-nowrap px-3'
            style={{
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
              marginLeft: 'calc(-1 * var(--bs-gutter-x) / 2)',
              marginRight: 'calc(-1 * var(--bs-gutter-x) / 2)',
              width: 'calc(100% + var(--bs-gutter-x))',
            }}
          >
            <Badge
              variant={activeTab === 'all' ? 'default' : 'outline'}
              as='button'
              type='button'
              onClick={() => handleTabChange('all')}
            >
              <i className='fe fe-home' />
            </Badge>
            {cases.length > 0 && (
              <Badge
                variant={activeTab === 'cases' ? 'default' : 'outline'}
                as='button'
                type='button'
                onClick={() => handleTabChange('cases')}
              >
                Cases
              </Badge>
            )}
            <Badge
              variant={activeTab === 'profile' ? 'default' : 'outline'}
              as='button'
              type='button'
              onClick={() => handleTabChange('profile')}
            >
              Profile
            </Badge>
            {creditReport && (
              <Badge
                variant={activeTab === 'credit_report' ? 'default' : 'outline'}
                as='button'
                type='button'
                onClick={() => handleTabChange('credit_report')}
              >
                Credit Score
              </Badge>
            )}
            {financialReport && financialReport.status == 'available' && (
              <Badge
                variant={activeTab === 'financial_report' ? 'default' : 'outline'}
                as='button'
                type='button'
                onClick={() => handleTabChange('financial_report')}
              >
                Budget
              </Badge>
            )}
            {unorganizedDocuments.length > 0 && (
              <Badge
                variant={activeTab === 'documents' ? 'default' : 'outline'}
                as='button'
                type='button'
                onClick={() => handleTabChange('documents')}
              >
                Unassigned
              </Badge>
            )}
            {affiliateOffers?.length > 0 && (
              <Badge
                variant={activeTab === 'deals' ? 'default' : 'outline'}
                as='button'
                type='button'
                onClick={() => handleTabChange('deals')}
              >
                Deals
              </Badge>
            )}
          </div>
          {alertParams.message && (
            <div className='mb-3'>
              <Alert
                type={
                  alertParams.success === true
                    ? 'success'
                    : alertParams.success === false
                      ? 'danger'
                      : 'primary'
                }
              >
                {alertParams.message}
              </Alert>
            </div>
          )}
        </div>
      </div>

      {activeTab === 'all' && (
        <CallToActionCards creditReportSolosettles={creditReportSolosettles} />
      )}

      <div className='row mb-8'>
        {loading ? (
          <div className='col'>
            <Card>
              <CardContent>
                <div id='loading-placeholder' className='placeholder-glow'>
                  <div className='d-flex align-items-center mb-3'>
                    <span className='placeholder col-4 mb-0'></span>
                  </div>
                  <div className='mb-3'>
                    <span className='placeholder col-6 d-block mb-2'></span>
                    <span className='placeholder col-8 d-block'></span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className='col'>
            {['all', 'cases'].includes(activeTab) &&
              cases.map(consumer_case => (
                <div key={consumer_case.id} className='mb-6'>
                  <div className='accordion shadow-none' id={`accordion-${consumer_case.id}`}>
                    <div className='accordion-item d-flex flex-column gap-1 shadow-none bg-transparent'>
                      <button
                        className='accordion-button accordion-button-left accordion-reverse shadow-none bg-transparent p-0 border-0 text-decoration-none d-flex align-items-center w-100 mb-2'
                        type='button'
                        data-bs-toggle='collapse'
                        data-bs-target={`#collapse-${consumer_case.id}`}
                        aria-expanded='true'
                        aria-controls={`collapse-${consumer_case.id}`}
                      >
                        <CaseHeader info={consumer_case.info} />
                      </button>
                      <div
                        id={`collapse-${consumer_case.id}`}
                        className='accordion-collapse collapse show'
                        aria-labelledby={`heading-${consumer_case.id}`}
                        data-bs-parent={`#accordion-${consumer_case.id}`}
                      >
                        <div className='accordion-body p-0 d-flex flex-column gap-4'>
                          {consumer_case.has_payment_plan && (
                            <Card>
                              <CardContent>
                                {consumer_case.documents.map(
                                  doc =>
                                    doc.payment_plan && (
                                      <PaymentPlanSummary
                                        key={doc.id}
                                        paymentPlan={doc.payment_plan}
                                      />
                                    )
                                )}
                              </CardContent>
                            </Card>
                          )}
                          {consumer_case.documents.map(document => (
                            <Card key={document.id}>
                              <CardContent>
                                <DocumentCard document={document} />
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

            {['all', 'documents'].includes(activeTab) && unorganizedDocuments.length > 0 && (
              <>
                {cases.length > 0 && activeTab == 'all' && <hr className='my-6' />}
                {unorganizedDocuments.map(document => (
                  <Card key={document.id} className='mb-6'>
                    <CardContent>
                      <DocumentCard document={document} />
                    </CardContent>
                  </Card>
                ))}
              </>
            )}

            {['all', 'profile'].includes(activeTab) && (
              <>
                {(unorganizedDocuments.length > 0 || cases.length > 0) && activeTab == 'all' && (
                  <hr className='my-6' />
                )}
                <ProfileEdit initialData={userProfile} onProfileUpdate={handleProfileUpdate} />
                {activeTab == 'profile' && <AccountActions />}
              </>
            )}

            {['all', 'credit_report'].includes(activeTab) && (
              <>
                {activeTab == 'all' && <hr className='my-6' />}
                <CreditReport creditReport={creditReport} />
              </>
            )}

            {financialReport &&
              financialReport.status == 'available' &&
              ['all', 'financial_report'].includes(activeTab) && (
                <>
                  {activeTab == 'all' && <hr className='my-6' />}
                  <FinancialReport financialReport={financialReport} />
                </>
              )}
            {affiliateOffers?.length > 0 && activeTab === 'deals' && (
              <AffiliateOfferCards affiliateOffers={affiliateOffers} />
            )}
          </div>
        )}
      </div>

      <div className='row' id='create'>
        <div className='col'>
          <Card>
            <CardContent>
              <CardTitle>
                <h2>Create a New Document</h2>
              </CardTitle>
              <CardDescription className='d-flex flex-column gap-2'>
                <span>
                  <a
                    href='/customer_documents?document_name=debt_answer'
                    className='h4 text-primary text-decoration-none d-block mb-0'
                    data-method='post'
                    rel='nofollow'
                  >
                    Debt Answer
                  </a>
                  <p className='mb-0'>Use this document to respond to a debt collection lawsuit.</p>
                </span>

                <span>
                  <a
                    href='/customer_documents?document_name=solosettle'
                    className='h4 text-primary text-decoration-none d-block mb-0'
                    data-method='post'
                    rel='nofollow'
                  >
                    SoloSettle
                  </a>
                  <p className='mb-0'>Use this to settle a debt.</p>
                </span>

                <span>
                  <a
                    href='/customer_documents?document_name=document_generation'
                    className='h4 text-primary text-decoration-none d-block mb-0'
                    data-method='post'
                    rel='nofollow'
                  >
                    Document Generation
                  </a>
                  <p className='mb-0'>Generate documents with AI.</p>
                </span>
              </CardDescription>
            </CardContent>
          </Card>
        </div>
      </div>

      {affiliateOffers?.length > 0 && activeTab !== 'deals' && (
        <>
          <hr className='my-6' />
          <AffiliateOfferCards affiliateOffers={affiliateOffers} />
        </>
      )}
    </div>
  )
}

export default Dashboard
